import { useEffect, useRef } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';

const CALENDAR_URL = 'https://calendar.app.google/DtBNRYcoX9a3oD5a9';

/* ─── Bullet item renderer ─────────────────────────────── */
const BulletItem = ({ item, iconColor }) => {
  const label = typeof item === 'string' ? null : item.label;
  const text  = typeof item === 'string' ? item  : item.text;
  return (
    <li className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
      <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: iconColor }} />
      <span>
        {label && <strong className="font-semibold" style={{ color: '#1F2937' }}>{label}:&nbsp;</strong>}
        {text}
      </span>
    </li>
  );
};

/* ─── Individual content block renderer ────────────────── */
const ContentBlock = ({ block, iconColor }) => {
  switch (block.type) {
    case 'p':
      return (
        <p className="text-base leading-relaxed mb-4" style={{ color: '#374151' }}>
          {block.text}
        </p>
      );

    case 'h2':
      return (
        <h2
          className="text-xl font-bold mt-8 mb-3"
          style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
        >
          {block.text}
        </h2>
      );

    case 'h3':
      return (
        <h3
          className="text-base font-semibold mt-5 mb-2"
          style={{ color: '#374151' }}
        >
          {block.text}
        </h3>
      );

    case 'bullets':
      return (
        <ul className="space-y-2.5 mb-5 pl-1">
          {block.items.map((item, i) => (
            <BulletItem key={i} item={item} iconColor={iconColor} />
          ))}
        </ul>
      );

    case 'table':
      return (
        <div className="overflow-x-auto mb-6 rounded-xl border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                {block.headers.map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-2.5 font-semibold text-xs uppercase tracking-wide border-b"
                    style={{ color: '#374151', borderColor: '#E5E7EB' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: ri < block.rows.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5" style={{ color: '#4B5563' }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'callout':
      return (
        <div
          className="rounded-xl p-5 mb-6"
          style={{ backgroundColor: '#F0F7FF', borderLeft: `4px solid ${iconColor}` }}
        >
          <p className="text-sm font-bold mb-2" style={{ color: iconColor }}>
            {block.title}
          </p>
          <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>
            {block.text}
          </p>
          {block.bullets && (
            <ul className="mt-3 space-y-2">
              {block.bullets.map((item, i) => (
                <BulletItem key={i} item={item} iconColor={iconColor} />
              ))}
            </ul>
          )}
        </div>
      );

    case 'numSection':
      return (
        <div className="mb-6 pl-0">
          <div className="flex items-start gap-4">
            <span
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mt-0.5"
              style={{ backgroundColor: iconColor }}
            >
              {block.number}
            </span>
            <div className="flex-1">
              <h4 className="font-semibold text-base mb-1.5" style={{ color: '#1F2937' }}>
                {block.title}
              </h4>
              <p className="text-sm leading-relaxed mb-3" style={{ color: '#4B5563' }}>
                {block.text}
              </p>
              {block.bullets && (
                <ul className="space-y-2 pl-1">
                  {block.bullets.map((item, i) => (
                    <BulletItem key={i} item={item} iconColor={iconColor} />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};

/* ─── ServiceModal ──────────────────────────────────────── */
const ServiceModal = ({ service, onClose }) => {
  const scrollRef = useRef(null);

  /* Lock body scroll, handle ESC key */
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  /* Scroll to top when a new service opens */
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [service]);

  if (!service) return null;

  const { Icon, iconColor, bgColor, subtitle, fullTitle, content, ctaText } = service;

  return (
    <div
      data-testid="service-modal-overlay"
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: '#FFFFFF', animation: 'modalFadeIn 0.28s ease' }}
    >
      {/* ── Colored top accent bar ── */}
      <div className="h-1 flex-shrink-0" style={{ backgroundColor: iconColor }} />

      {/* ── Header ── */}
      <header
        className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: '#F3F4F6', backgroundColor: '#FFFFFF' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: bgColor }}
          >
            <Icon size={18} style={{ color: iconColor }} />
          </div>
          <div>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: bgColor, color: iconColor }}
            >
              {subtitle}
            </span>
          </div>
        </div>

        <button
          data-testid="service-modal-close"
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-gray-100"
          aria-label="Close"
        >
          <X size={18} style={{ color: '#6B7280' }} />
        </button>
      </header>

      {/* ── Scrollable Content ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 pt-8 pb-32">
          {/* Full title */}
          <h1
            className="text-2xl sm:text-3xl font-bold mb-8 leading-snug"
            style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}
          >
            {fullTitle}
          </h1>

          {/* Content blocks */}
          {content.map((block, i) => (
            <ContentBlock key={i} block={block} iconColor={iconColor} />
          ))}
        </div>
      </div>

      {/* ── Sticky CTA Footer ── */}
      <div
        className="flex-shrink-0 border-t"
        style={{
          borderColor: '#E5E7EB',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
        }}
      >
        <a
          data-testid="service-modal-cta"
          href={CALENDAR_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:opacity-90"
          style={{ backgroundColor: iconColor }}
        >
          {ctaText}
          <ArrowRight size={14} />
        </a>
      </div>

      {/* Fade-in keyframe */}
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ServiceModal;
